import clsx from "clsx";
import {
  createElement,
  CSSProperties,
  ElementRef,
  forwardRef,
  ForwardRefRenderFunction,
} from "react";
import { AtomicProps, atoms, DynamicProps, fillings, reset } from "./atoms.css";

type DynamicProperties = keyof DynamicProps;
type AtomicProperties = keyof AtomicProps;

type KnownCSSProperties = AtomicProperties | DynamicProperties;

export interface BoxAtoms
  extends AtomicProps,
    DynamicProps,
    Omit<CSSProperties, KnownCSSProperties> {}

export type BoxProps<T extends keyof JSX.IntrinsicElements = "div"> = Omit<
  JSX.IntrinsicElements[T],
  "ref" | "as" | "css"
> & {
  as?: T;
  css?: BoxAtoms;
  ref?: React.Ref<HTMLElement | ElementRef<T>>;
};

const RenderFunction: ForwardRefRenderFunction<HTMLElement, BoxProps<"div">> = (
  {
    as = "div",
    css,
    className: userClassName,
    style: userStyles,
    children,
    ...props
  },
  ref,
) => {
  const atomicStyles: Record<string, unknown> = {};
  const dynamicStyles: Record<string, unknown> = {};
  const customStyles: Record<string, unknown> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in css) {
    if (atoms.properties.has(key as AtomicProperties)) {
      atomicStyles[key] = css[key as keyof typeof css];
    } else if (fillings.properties.includes(key as DynamicProperties)) {
      const dynamicValue = css[key as keyof typeof css];
      // Convert numeric values to pixel values for *width and *height
      const value =
        [
          "top",
          "right",
          "bottom",
          "left",
          "width",
          "height",
          "minWidth",
          "minHeight",
          "maxWidth",
          "maxHeight",
        ].includes(key) && typeof dynamicValue === "number"
          ? `${dynamicValue}px`
          : dynamicValue;
      dynamicStyles[key] = value;
    } else {
      customStyles[key] = css[key as keyof typeof css];
      // Add a warning in development mode
      if (debug && process.env.NODE_ENV === "development") {
        console.info(
          `The CSS property "${key}" is not a valid atomic property and will be passed to "style" attribute directly.`,
        );
      }
    }
  }

  const sprinkleClass = atoms(atomicStyles);

  const { className: fillingClassName, style: inlineStyles } =
    fillings(dynamicStyles);

  return createElement(
    as,
    {
      style: { ...inlineStyles, ...customStyles, ...userStyles },
      className: clsx(reset, sprinkleClass, fillingClassName, userClassName),
      ref,
      ...props,
    },
    children,
  );
};

const debug = false;

export const Box = forwardRef(RenderFunction) as <
  T extends keyof JSX.IntrinsicElements,
>(
  props: BoxProps<T>,
) => React.ReactElement;
